(ns manage.adset-decision-tree-page.view.predicate-editor.core
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/List$default" :as List]
            ["@mui/material/ListItem$default" :as ListItem]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/x-date-pickers-pro" :refer [LocalizationProvider]]
            ["@mui/x-date-pickers-pro/AdapterMoment" :refer [AdapterMoment]]
            ["moment-timezone$default" :as moment]
            ["react-hook-form" :as rhf]
            [common.form :as form]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.context :as context]
            [manage.adset-decision-tree-page.view.predicate-editor.converter
             :refer [schema-output->cf-schema
                     trigger->selector
                     empty-predicate
                     adjust-schema-to-property]]
            [manage.adset-decision-tree-page.view.predicate-editor.input-components
             :as input-components]
            [manage.adset-decision-tree-page.view.side-panel.property-form
             :refer [get-property-field-name]]
            [manage.adset-decision-tree-page.view.side-panel.util
             :refer [condition-group-item]]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def operands-sub-paths [:value :start :end])

(defn reset-fields
  [resetField paths]
  (doseq [path paths]
    (resetField path #js {:defaultValue nil})))

(defui predicate-input-form
  [{{:keys [resetField control] :as form-control} :form-control
    :keys [schema-output get-field-path property render-alert parent-node]}]
  (let [schema (schema-output->cf-schema schema-output)
        property-disabled (:disabled property)

        trigger-field-path (get-field-path :trigger)
        trigger (keyword (rhf/useWatch #js {:name trigger-field-path :control control}))
        selector (trigger->selector trigger)

        operator-field-path (get-field-path :operator)
        get-operands-field-path (uix/use-callback #(get-field-path :operands %)
                                                  [get-field-path])
        operands-paths (uix/use-memo #(map get-operands-field-path operands-sub-paths)
                                     [get-operands-field-path])

        dependency-handler
        (uix/use-callback
         (fn [_ {changed-field-path :name}]
           (condp = changed-field-path
             trigger-field-path (reset-fields resetField (conj operands-paths
                                                               operator-field-path))
             operator-field-path (reset-fields resetField operands-paths)
             nil))
         [trigger-field-path operator-field-path operands-paths resetField])

        clear-condition
        (uix/use-callback
         #(reset-fields resetField (conj operands-paths operator-field-path trigger-field-path))
         [resetField operands-paths operator-field-path trigger-field-path])]
    (form/use-form-callback form-control dependency-handler {:change-only true})
    ($ Stack {:spacing 1}
       ($ input-components/trigger-select {:schema-output schema-output
                                           :control control
                                           :disabled property-disabled
                                           :allow-any-trigger (:allow-any-trigger property)
                                           :field-path trigger-field-path})
       ($ input-components/operator-select {:selector selector
                                            :schema schema
                                            :control control
                                            :disabled property-disabled
                                            :field-path operator-field-path})
       ($ input-components/operands-input {:form-controls form-control
                                           :trigger trigger
                                           :get-field-path get-operands-field-path
                                           :operator-field-path operator-field-path
                                           :schema schema
                                           :disabled property-disabled
                                           :parent-node parent-node})
       (when render-alert
         (render-alert {:trigger trigger :on-clear clear-condition})))))

(defui predicate-editor-item [{:keys [index property] :as props}]
  (let [get-field-path (uix/use-callback
                        (fn [& args]
                          (apply get-property-field-name property (str index) args))
                        [property index])]
    ($ condition-group-item (select-keys props [:on-delete :delete-disabled :label])
       ($ predicate-input-form (assoc props :get-field-path get-field-path)))))

(defui predicate-editor-form [{:keys [form-control property schema-output render-alert selected-node]
                               {:keys [fields append remove]} :field-array}]
  (let [property-disabled (:disabled property)
        context-schema (context/use-property-context property)
        schema-output (or schema-output
                          (:output (adjust-schema-to-property property context-schema)))
        ;; Get parent decision node to access timezone configuration
        parent-node (use-subscribe [::sub/node-data (:parent-id selected-node)])
        item-base-prop (cond-> {:form-control form-control
                                :delete-disabled (or property-disabled (<= (count fields) 1))
                                :schema-output schema-output
                                :property property
                                :parent-node parent-node}
                         render-alert (assoc :render-alert render-alert))]
    ($ LocalizationProvider {:date-adapter AdapterMoment
                             :date-lib-instance moment
                             :adapter-locale (.-language js/navigator)}
       ($ List
          (map-indexed
           (fn [index ^js field]
             ($ predicate-editor-item
                (assoc item-base-prop
                       :key (.-id field)
                       :label (if (zero? index) "if" "and")
                       :index index
                       :on-delete (partial remove index))))

           (or (seq fields) [#js {:id "inititial"}]))
          (when-not property-disabled
            ($ ListItem
               ($ Button {:startIcon ($ Icon "add")
                          :variant "text"
                          :on-click (partial append empty-predicate)}
                  "More Conditions")))))))

(defui predicate-editor
  [{:keys [selected-item selected-node form-control property field-array]}]
  ($ predicate-editor-form {:key [selected-item (:id property)]
                            :property property
                            :form-control form-control
                            :field-array field-array
                            :selected-node selected-node}))
